import type { Dispatch } from 'react';

import type { TableColumn } from '@equip.health/ui';

import type { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';

import type { PatientCoveragePriority } from '../util/patientCoverage.util';
import type { AvatarUserType } from '@equip.health/ui/lib/types/src/components/Avatar';

export enum PatientTabs {
  OVERVIEW = 'overview',
  PROFILE = 'profile',
  CLINICAL_PROFILE = 'clinical-profile',
  MEDICATIONS = 'medications',
  JOURNAL = 'journal',
  LABS = 'labs',
  ASSESSMENTS = 'assessments',
  ENROLLMENT = 'enrollment',
  CARE_PLAN = 'care-plan',
  TASKS = 'tasks',
  APPOINTMENTS = 'appointments',
  MESSAGES = 'messages',
  NOTES = 'notes',
  DOCUMENTS = 'documents',
  ACTIVITY = 'activity',
}

// TODO replace this with the equip-ui version, soon to be exported
export enum PatientMatchingFields {
  CONTACT_EMAIL = 'CONTACT_EMAIL',
  CONTACT_PHONE_NUMBER = 'CONTACT_PHONE_NUMBER',
  PATIENT_DATE_OF_BIRTH = 'PATIENT_DATE_OF_BIRTH',
  PATIENT_EMAIL = 'PATIENT_EMAIL',
  PATIENT_FIRST_NAME = 'PATIENT_FIRST_NAME',
  PATIENT_LAST_NAME = 'PATIENT_LAST_NAME',
  PATIENT_PHONE_NUMBER = 'PATIENT_PHONE_NUMBER',
}

export interface PatientTabProps {
  assessmentList: SurveyResponse[];
  isAssessmentError: boolean;
  isAssessmentLoading: boolean;
  onSendEmail: (assessment: Partial<SurveyResponse>) => void;
  onUpdateAssessmentStatus: (
    status: SurveyResponseStatus,
    toUserEmail: string,
    responseExternalId: string,
  ) => void;
  patient: Patient;
  patientClinicalProfile: PatientClinicalProfile;
  patientEmail: string;
  patientFirstName: string;
  patientId: string;
  patientLastName: string;
  patientPhone: string;
  patientPreferredFullName: string;
  patientTasks?: Task[];
  selectedSurveyResults: Partial<SurveyResponse>;
  surveys: Survey[];
  // for creating new assessments, and perhaps other uses. TODO this can be a context. this is passed many levels down
  updateAssessmentState: Dispatch<AssessmentAction>;
}

export type PatientTabsDefinition = {
  label: string;
  subPath: string;
};

export const basePatientTabsCollection: Record<
  PatientTabs,
  PatientTabsDefinition
> = {
  [PatientTabs.OVERVIEW]: {
    label: 'Overview',
    subPath: 'overview',
  },
  [PatientTabs.PROFILE]: {
    label: 'Patient profile',
    subPath: 'profile',
  },
  [PatientTabs.CLINICAL_PROFILE]: {
    label: 'Clinical',
    subPath: 'clinical-profile',
  },
  [PatientTabs.MEDICATIONS]: {
    label: 'Medications',
    subPath: 'medications',
  },
  [PatientTabs.LABS]: {
    label: 'Labs',
    subPath: 'labs',
  },
  [PatientTabs.JOURNAL]: {
    label: 'Journal',
    subPath: 'journal',
  },
  [PatientTabs.ASSESSMENTS]: {
    label: 'Assessments',
    subPath: 'assessments',
  },
  [PatientTabs.ENROLLMENT]: {
    label: 'Enrollment',
    subPath: 'enrollment',
  },
  [PatientTabs.CARE_PLAN]: {
    label: 'Care planning',
    subPath: 'care-planning',
  },
  [PatientTabs.TASKS]: {
    label: 'Tasks',
    subPath: 'tasks',
  },
  [PatientTabs.APPOINTMENTS]: {
    label: 'Calendar',
    subPath: 'appointments',
  },
  [PatientTabs.MESSAGES]: {
    label: 'Messages',
    subPath: 'messages',
  },
  [PatientTabs.NOTES]: {
    label: 'Notes',
    subPath: 'notes',
  },
  [PatientTabs.DOCUMENTS]: {
    label: 'Documents',
    subPath: 'documents',
  },
  [PatientTabs.ACTIVITY]: {
    label: 'Activity',
    subPath: 'activity',
  },
};

export const iframeContainerTabs = new Set([
  PatientTabs.MEDICATIONS.toString(),
]);

export enum ConsentToRecord {
  ConsentGiven = 'Consent given',
  ConsentNotGiven = 'Consent not given',
}

export enum EpisodeTreatmentProgram {
  EQUIP = 'Equip treatment',
  FREEFORM = 'Freeform',
  RELAPSE_PREVENTION = 'Relapse prevention',
  EQUIP_ADULT = 'Equip Adult',
  OPEN_CARE_MODEL = 'Open Care Model',
  TRANSITION = 'Transition Track',
}

export enum CommonTreatmentProgram {
  EQUIP_TREATMENT = 'Equip treatment',
  EQUIP_ADULT = 'Equip Adult',
}

export enum CommonLanguage {
  ENGLISH = 'English',
}

export const EpisodeTreatmentProgramOptions = Object.values(
  EpisodeTreatmentProgram,
);
export enum EpisodeStatus {
  NEW = 'New',
  ASSESSING = 'Assessing',
  ENROLLED = 'Enrolled',
  IN_TREATMENT = 'In-treatment',
  PAUSED = 'Paused',
  CLOSED = 'Closed',
}

export enum EpisodeInsuranceVerificationStatus {
  APPROVED = 'Approved',
  DENIED = 'Denied',
  NOT_READY = 'Not ready',
  PRE_AUTHORIZATION_IN_PROGRESS = 'Pre-authorization in progress',
  READY_FOR_REVIEW = 'Ready for review',
  VERIFICATION_IN_PROGRESS = 'Verification in progress',
}

enum BaseClearanceStatus {
  CLEARED = 'Cleared',
  IN_PROGRESS = 'In progress',
  NOT_CLEARED = 'Not cleared',
  NOT_READY = 'Not ready',
  PRE_AUTHORIZATION_IN_PROGRESS = 'Pre-authorization in progress',
  READY_FOR_REVIEW = 'Ready for review',
}

export const EpisodeMedicalClearanceStatus = BaseClearanceStatus;
export type EpisodeMedicalClearanceStatus = BaseClearanceStatus;

export const EpisodePsychClearanceStatus = BaseClearanceStatus;
export type EpisodePsychClearanceStatus = BaseClearanceStatus;

type BaseClearanceDetails = {
  completedDate?: string;
  isCompletedDateValid?: boolean;
  isRequestedDateValid?: boolean;
  requestedDate?: string;
};

export type EpisodeMedicalClearance = BaseClearanceDetails & {
  medicalClearanceStatus?: EpisodeMedicalClearanceStatus;
};

export type EpisodePsychClearance = BaseClearanceDetails & {
  psychClearanceStatus?: EpisodePsychClearanceStatus;
};

export type InsuranceVerificationDetails = BaseClearanceDetails & {
  insuranceVerificationStatus?: EpisodeInsuranceVerificationStatus;
};

export type AdminEpisode = {
  authorizationDetails?: EpisodeAuthorization[];
  createdAt?: string;
  episodeClosedDate: string | null;
  episodeExternalId: string;
  episodeStartDate: string;
  episodeStatus: EpisodeStatus;
  existingCoverageId?: string;
  firstInquiryReferralDate: string;
  insuranceVerification?: InsuranceVerificationDetails;
  isPaused?: boolean;
  lengthInWeeks?: number;
  level?: string;
  medicalClearance?: EpisodeMedicalClearance;
  patientExternalId: string;
  pausedDetails?: EpisodePausedDetails[];
  payorName?: string;
  payorProgramName?: string;
  psychClearance?: EpisodePsychClearance;
  statusReason?: string;
  treatmentProgramName: string;
};

export type PatientCoverage = {
  alternativeId?: string;
  coverageExternalId?: string;
  coveragePriority?: PatientCoveragePriority;
  customPayorName?: string;
  endDate?: string;
  groupId?: string;
  inquiryId?: string;
  isActive?: boolean;
  isInNetwork?: boolean;
  payor?: string;
  payorProgram?: string;
  payorExternalId?: string;
  providerServicesPhoneNumber?: string;
  patientRelationToSubscriber?: string;
  subscriberDateOfBirth?: string;
  subscriberFirstName?: string;
  subscriberLastName?: string;
  startDate?: string;
  stateCode?: string;
  subscriberId?: string;
};

export const PATIENT_SEARCH_PAGE_SIZE = 20;

export enum WarningTypes {
  UNDER_18 = 'under_18',
  NO_RECENT_WEIGHT = 'no_recent_weight',
  EXCEEDS_SCALE_LIMIT = 'exceeds_scale_limit',
}
export enum OrderSteps {
  WARNING = 'warning',
  SHIPPING_ADDRESS = 'shipping_address',
  ENTER_MANUAL_ADDRESS = 'enter_manual_address',
  REVIEW_ORDER = 'review_order',
}
export const AvatarUserTypeDisplay: Record<AvatarUserType, string> = {
  patient: 'Patient',
  provider: 'Provider',
  support: 'Support',
};
export enum UserRole {
  PATIENT = 'Patient',
  PRIMARY_CONTACT = 'Primarycontact',
  PROXY = 'Proxy',
}
export enum OrderEquipScaleFrom {
  BANNER = 'banner',
  DEVICE = 'device',
  NONE = '',
}
export interface ReorderContext {
  isReorder: boolean;
  reorderReasonCode: string;
}
export enum EquipScaleOrderStatus {
  REQUESTED = 'Requested',
  SHIPPED = 'Shipped',
  DELIVERED = 'Delivered',
  ACTIVE = 'Active',
  ORDERED = 'Ordered',
  CONFIRMED = 'Confirmed',
  ORDER_CANCELED = 'Order canceled',
  DELIVERY_FAILED = 'Delivery failed',
  PROCESSING_FAILED = 'Processing failed',
  TERMINATED = 'Terminated',
}
export const TITLE_EXCEEDS_SCALE_LIMIT =
  'Scale may not be suitable for this patient';
export const TITLE_ENTER_MANUAL_ADDRESS = 'Enter address manually';
export const TITLE_NO_RECENT_WEIGHT = 'No recent weight data available';
export const TITLE_PATIENT_UNDER_18 = 'Patient is under 18';
export const TITLE_REVIEW_ORDER = 'Review and Confirm Order';
export const TITLE_SHIPPING_ADDRESS = 'Choose shipping address';
export const TITLE_WARNING = 'No recent weight data available';
export const ORDER_PENDING_CONFIRMATION_TEXT = 'Ordered, pending confirmation';
export const ORDERED_TEXT = 'Ordered';

export const REORDER_EQUIP_SCALE = 'Reorder scale';
export const EQUIP_SCALE_REORDER_TEXT =
  'Are you sure you would like to reorder an Equip scale for this patient? If the patient is experiencing issues using their scale, please contact Equip Support directly.';

export const ORDER_LAB_TEXT = 'Order lab';
export const LAB_TAB_HEADING = 'Labs';
export const NO_LABS_ORDERED_MESSAGE = 'No labs have been ordered yet.';
export const NEW_ORDER_LAB_TEXT = 'New order';
export const LAB_ORDER_SUCCESS_MESSAGE = 'Lab order submitted successfully.';
export const VIEW_LABS_TEXT = 'View labs';
export const GO_TO_PATIENT_PROFILE = 'Go to patient profile';
export const GENERATE_PATIENT_REPORT = 'Generate patient report';

export const warningTitleMap: Record<WarningTypes, string> = {
  [WarningTypes.EXCEEDS_SCALE_LIMIT]: TITLE_EXCEEDS_SCALE_LIMIT,
  [WarningTypes.NO_RECENT_WEIGHT]: TITLE_NO_RECENT_WEIGHT,
  [WarningTypes.UNDER_18]: TITLE_PATIENT_UNDER_18,
};
export interface Address {
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber?: string;
  email?: string;
  userRole?: UserRole;
  firstName?: string;
  lastName?: string;
  chosenName?: string;
  address?: string | GooglePlacesField;
}

export interface CustomShippingAddress extends Address {
  latitude: number;
  longitude: number;
}

export const stepTitleMap: Record<OrderSteps, string> = {
  [OrderSteps.ENTER_MANUAL_ADDRESS]: TITLE_ENTER_MANUAL_ADDRESS,
  [OrderSteps.REVIEW_ORDER]: TITLE_REVIEW_ORDER,
  [OrderSteps.SHIPPING_ADDRESS]: TITLE_SHIPPING_ADDRESS,
  [OrderSteps.WARNING]: TITLE_WARNING,
};

export type PatientListFiltersState = {
  admitDateFrom: PatientFilterType;
  admitDateTo: PatientFilterType;
  careTeamMember: PatientFilterType[];
  episodeLevels: string[];
  episodeStatus: PatientFilterType[];
  episodeStatusReasons: string[];
  insuranceVerificationStatus: string[];
  isPatientPaused: boolean;
  medicalClearanceStatus: string[];
  name: string;
  payor: string[];
  payorProgram: string[];
  primaryDiagnosis: PatientFilterType[];
  psychClearanceStatuses: string[];
  treatmentPrograms: string[];
  universalStatuses: PatientFilterType[];
};

export enum PatientListFilterReducerActions {
  UPDATE_ADMIT_DATE_FROM,
  UPDATE_ADMIT_DATE_TO,
  UPDATE_ALL_FILTER,
  UPDATE_CARE_TEAM_MEMBER,
  UPDATE_DIAGNOSIS,
  UPDATE_EPISODE_LEVEL,
  UPDATE_EPISODE_STATUS_REASON,
  UPDATE_EPISODE_STATUS,
  UPDATE_INSURANCE_VERIFICATION_STATUS,
  UPDATE_IS_PATIENT_PAUSED,
  UPDATE_MEDICAL_CLEARANCE_STATUS,
  UPDATE_NAME,
  UPDATE_PAYOR_PROGRAM,
  UPDATE_PAYOR,
  UPDATE_PSYCH_CLEARANCE_STATUSES,
  UPDATE_TREATMENT_PROGRAM,
  UPDATE_UNIVERSAL_STATUS,
}

export enum PatientUniversalStatusCode {
  NEW = 'NEW',
  FIELDED = 'FIELDED',
  QUALIFIED = 'QUALIFIED',
  ASSESSING = 'ASSESSING',
  ONBOARDING = 'ONBOARDING',
  CLOSED = 'CLOSED',
  IN_TREATMENT = 'IN_TREATMENT',
  DISCHARGED = 'DISCHARGED',
  PAUSED = 'PAUSED',
  NEVER_STARTED = 'NEVER_STARTED',
  UNKNOWN = 'UNKNOWN',
}

export const openUniversalStatuses = [
  PatientUniversalStatusCode.NEW,
  PatientUniversalStatusCode.FIELDED,
  PatientUniversalStatusCode.QUALIFIED,
  PatientUniversalStatusCode.ASSESSING,
  PatientUniversalStatusCode.ONBOARDING,
  PatientUniversalStatusCode.IN_TREATMENT,
  PatientUniversalStatusCode.PAUSED,
];

export enum CareTeamProviderType {
  ADMISSIONS_OPERATIONS_COORDINATOR = 'admissions operations',
  ADMISSIONS_SPECIALIST = 'admissions specialist',
  ADMISSIONS_SUPERVISOR = 'admissions supervisor',
  DIETITIAN = 'dietitian',
  FAMILY_EXPERIENCE_NAVIGATOR = 'family experience navigator',
  FAMILY_MENTOR = 'family mentor',
  MEDICAL_ASSISTANT = 'medical assistant',
  MEDICAL_PROVIDER = 'medical provider',
  NURSE_PRACTITIONER = 'nurse practitioner',
  PATIENT_SERVICE_REPRESENTATIVE = 'patient service representative',
  PEER_MENTOR = 'peer mentor',
  PHYSICIAN = 'physician',
  PHYSICIAN_ASSISTANT = 'physician assistant',
  PROVIDER_PARTNERSHIPS = 'provider partnerships',
  PSYCHIATRIC_NURSE_PRACTITIONER = 'psychiatric nurse practitioner',
  PSYCHIATRIST = 'psychiatrist',
  REGISTERED_NURSE = 'registered nurse',
  THERAPIST = 'therapist',
  RECOVERY_COACH = 'recovery coach',
  CAREGIVER_COACH = 'caregiver coach',
  CARE_NAVIGATOR = 'care navigator',
}

export const CareTeamProviderTypeNames: Record<CareTeamProviderType, string> = {
  [CareTeamProviderType.ADMISSIONS_SPECIALIST]: 'Admissions Specialist',
  [CareTeamProviderType.ADMISSIONS_SUPERVISOR]: 'Admissions Supervisor',
  [CareTeamProviderType.ADMISSIONS_OPERATIONS_COORDINATOR]:
    'Admissions Operations',
  [CareTeamProviderType.DIETITIAN]: 'Dietitian',
  [CareTeamProviderType.FAMILY_EXPERIENCE_NAVIGATOR]:
    'Family Experience Navigator',
  [CareTeamProviderType.FAMILY_MENTOR]: 'Family Mentor',
  [CareTeamProviderType.MEDICAL_ASSISTANT]: 'Medical Assistant',
  [CareTeamProviderType.MEDICAL_PROVIDER]: 'Medical Provider',
  [CareTeamProviderType.NURSE_PRACTITIONER]: 'Nurse Practitioner',
  [CareTeamProviderType.PATIENT_SERVICE_REPRESENTATIVE]:
    'Patient Service Representative',
  [CareTeamProviderType.PEER_MENTOR]: 'Peer Mentor',
  [CareTeamProviderType.PHYSICIAN]: 'Physician',
  [CareTeamProviderType.PHYSICIAN_ASSISTANT]: 'Physician Assistant',
  [CareTeamProviderType.PROVIDER_PARTNERSHIPS]: 'Provider Partnerships',
  [CareTeamProviderType.PSYCHIATRIC_NURSE_PRACTITIONER]:
    'Psychiatric Nurse Practitioner',
  [CareTeamProviderType.PSYCHIATRIST]: 'Psychiatrist',
  [CareTeamProviderType.REGISTERED_NURSE]: 'Registered Nurse',
  [CareTeamProviderType.THERAPIST]: 'Therapist',
  [CareTeamProviderType.RECOVERY_COACH]: 'Recovery Coach',
  [CareTeamProviderType.CAREGIVER_COACH]: 'Caregiver Coach',
  [CareTeamProviderType.CARE_NAVIGATOR]: 'Care Navigator',
};

/** Reverse lookup: display name -> CareTeamProviderType enum */
export const CareTeamProviderTypeFromDisplayName: Record<
  string,
  CareTeamProviderType
> = Object.entries(CareTeamProviderTypeNames).reduce(
  (acc, [key, value]) => {
    acc[value] = key as CareTeamProviderType;
    return acc;
  },
  {} as Record<string, CareTeamProviderType>,
);

export const DEFAULT_CARE_TEAM_PROVIDER_TYPES: CareTeamProviderType[] = [
  CareTeamProviderType.THERAPIST,
  CareTeamProviderType.DIETITIAN,
  CareTeamProviderType.MEDICAL_PROVIDER,
  CareTeamProviderType.RECOVERY_COACH,
  CareTeamProviderType.CAREGIVER_COACH,
];

const tableHeadStyles = { fontSize: '10px', maxWidth: '192px' };

const TABLE_COLUMN_NAME_KEY = 'name';
const TABLE_COLUMN_PATIENT_STATUS_KEY = 'patientStatus';
const TABLE_COLUMN_PRIMARY_DIAGNOSIS_KEY = 'primaryDiagnosis';
const TABLE_COLUMN_STATE_KEY = 'state';
const TABLE_COLUMN_ADMIN_DATE_KEY = 'admitDate';

export const TABLE_DEFAULT_COLUMNS = [TABLE_COLUMN_NAME_KEY];
export const TABLE_INITIAL_COLUMNS = [
  TABLE_COLUMN_NAME_KEY,
  TABLE_COLUMN_PATIENT_STATUS_KEY,
  TABLE_COLUMN_PRIMARY_DIAGNOSIS_KEY,
  TABLE_COLUMN_STATE_KEY,
  TABLE_COLUMN_ADMIN_DATE_KEY,
];

const createTableColumn = <K extends string>(
  header: string,
  key: K,
  extraProps?: Partial<TableColumn<PatientRow>>,
): TableColumn<PatientRow> => ({
  ...tableHeadStyles,
  header,
  key: key as keyof PatientRow,
  ...extraProps,
});

export const tableHeads: TableColumn<PatientRow>[] = [
  createTableColumn('Name', TABLE_COLUMN_NAME_KEY, {
    isSticky: true,
    minWidth: '192px',
  }),
  createTableColumn('Admin episode status', 'adminEpisodeStatus'),
  createTableColumn('Patient status', 'universalStatus'),
  createTableColumn('Admit date', TABLE_COLUMN_ADMIN_DATE_KEY),
  createTableColumn('State', TABLE_COLUMN_STATE_KEY),
  createTableColumn('Primary diagnosis', TABLE_COLUMN_PRIMARY_DIAGNOSIS_KEY),
  createTableColumn(
    'Patient Service Representative',
    'patientServiceRepresentative',
  ),
  createTableColumn('Qualifying call note', 'qualifyingCallNote'),
  createTableColumn('Insurance company', 'payorName'),
  createTableColumn('Payor program', 'payorProgramName'),
  createTableColumn('Insurance status', 'insuranceStatus'),
  createTableColumn('Admissions Specialist', 'admissionsSpecialist'),
  createTableColumn('Phone screen note', 'phoneScreenNote'),
  createTableColumn('Intake survey', 'intakeSurvey'),
  createTableColumn('Presenting problem note', 'presentingProblemNote'),
  createTableColumn('Consent form note', 'consentFormNote'),
  createTableColumn('Medical clearance status', 'medicalClearanceStatus'),
  createTableColumn('Last note date', 'lastNoteDate'),
  createTableColumn('Last note', 'lastNote'),
  createTableColumn('FEN introduction note', 'fenIntroNote'),
  createTableColumn('Family Experience Navigator', 'familyExperienceNavigator'),
  createTableColumn('Dietitian', 'dietitian'),
  createTableColumn('Therapist', 'therapist'),
  createTableColumn('Recovery Coach', 'recoveryCoach'),
  createTableColumn('Caregiver Coach', 'caregiverCoach'),
  createTableColumn('Medical Provider', 'medicalProvider'),
  createTableColumn('Days of care', 'daysOfCare'),
  createTableColumn('Weeks at Equip', 'weeksAtEquip'),
  createTableColumn('Level', 'episodeLevel'),
  createTableColumn('Closed reason', 'episodeStatusReason'),
  createTableColumn('Psych clearance status', 'psychClearanceStatus'),
  createTableColumn('Active program name', 'treatmentProgramName'),
];

export const headerMapFromTableHeads = Object.fromEntries(
  tableHeads.map((head) => [head.key, head]),
);

export const headerTextKeyArray = tableHeads.map((head) => head.key);

export const EMPTY_DATE_FORMAT = '--/--/----';

export enum CreateAssessmentIntent {
  BEGIN_NOW,
  SEND_INVITATION,
}

export enum UpdateAssessmentIntent {
  SEND_INVITATION,
  UPDATE_RESPONDENT,
}

export type AssessmentState = {
  assessmentList: SurveyResponse[];
  createAssessmentIntent: CreateAssessmentIntent;
  emailSentData: Partial<SurveyResponse>;
  isAssessmentListError?: boolean;
  isAssessmentListLoading?: boolean;
  selectedAssessment: Partial<SurveyResponse>;
  selectedSurveyResults: Partial<SurveyResponse>;
  updateAssessmentIntent: UpdateAssessmentIntent;
};

export type AssessmentReducer = (
  state: AssessmentState,
  action: AssessmentAction,
) => AssessmentState;

export type AssessmentAction = {
  field: keyof AssessmentState | 'SET';
  data: any;
};

export const EDITBASICINFO_ADDRESS_REQUIRED_FIELDS = ['state'];

type requiredFieldsType = 'fax' | 'home' | 'mobile' | 'office' | 'other';

export const EDITBASICINFO_PHONES_REQUIRED_FIELDS: requiredFieldsType[] = [
  'fax',
  'home',
  'mobile',
  'office',
  'other',
];

export enum CareTeamType {
  ADMISSIONS = 'admissions',
  CARE = 'care',
}

export const PROFILE_SURVEY_BANNER_STYLES = {
  padding: '16px 24px',
};

export const patientSurveyMapping: Array<SurveyMapping> = [
  { key: 'chosenName', type: 'string', value: 'chosenName' },
  { key: 'sex', type: 'string', value: 'assignedGenderAtBirth' },
  { key: 'genderOnRecord', type: 'string', value: 'patientGender' },
  { key: 'pronounsOnRecord', type: 'array', value: 'patientPronouns' },
  { key: 'ethnicities', type: 'array', value: 'patientEthnicity' },
  { key: 'email', type: 'string', value: 'patientEmail' },
  { key: 'phoneNumber', type: 'string', value: 'patientPhone' },
  {
    key: 'contactPreferences',
    type: 'array',
    value: 'patientPreferredContact',
  },
  {
    key: 'languages',
    type: 'array',
    value: 'intake_pt_preferred_healthcare_lang',
  },
];

export const patientSurveyMappingValues: Array<SurveyMappingValues> = [
  {
    field: 'genderOnRecord',
    key: 'Cis-gender male',
    type: 'string',
    value: 'Boy/Man',
  },
  {
    field: 'genderOnRecord',
    key: 'Cis-gender female',
    type: 'string',
    value: 'Girl/Woman',
  },
  {
    field: 'genderOnRecord',
    key: 'Transgender male',
    type: 'string',
    value: 'Boy/Man-Trans',
  },
  {
    field: 'genderOnRecord',
    key: 'Transgender female',
    type: 'string',
    value: 'Girl/Woman-Trans',
  },
  {
    field: 'genderOnRecord',
    key: 'Unknown',
    type: 'string',
    value: 'Unknown/Questioning',
  },
  { field: 'genderOnRecord', key: 'Other', type: 'string', value: 'other' },
  {
    field: 'pronounsOnRecord',
    key: 'He/Him/His',
    type: 'string',
    value: 'he/him/his',
  },
  {
    field: 'pronounsOnRecord',
    key: 'She/Her/Hers',
    type: 'string',
    value: 'she/her/hers',
  },
  {
    field: 'pronounsOnRecord',
    key: 'They/Them/Theirs',
    type: 'string',
    value: 'they/them/theirs',
  },
  { field: 'pronounsOnRecord', key: 'Other', type: 'string', value: 'other' },
  { field: 'pronounsOnRecord', key: 'None', type: 'string', value: 'none' },
  {
    field: 'contactPreferences',
    key: 'Unknown',
    type: 'string',
    value: 'unknown',
  },
  {
    field: 'contactPreferences',
    key: 'No preference',
    type: 'string',
    value: 'none',
  },
  { field: 'sex', key: 'Unknown', type: 'string', value: 'UNK' },
  {
    field: 'ethnicities',
    key: 'Prefer not to answer',
    type: 'string',
    value: 'none',
  },
  {
    field: 'ethnicities',
    key: 'Other',
    type: 'string',
    value: 'other',
  },
];

export const MULTIPLE_PRONOUNS_DELIMITER = ', ';
export const EMPTY_STRING = '';

export const NOT_ON_FILE = 'Not on file';

export const PREFER_NOT_TO_ANSWER_LABEL = 'Prefer not to answer';

export const CHANGE_USER_EMAIL_WARNING =
  "App access cannot be changed while changing the user's email address";
export const SUPPORT_EMAIL_REQUIRED =
  'Email address and cell phone number are required. Please fill them out above.';
export const PATIENT_DATE_OF_BIRTH_REQUIRED =
  'A date of birth is required to create an Equip Account. Please add date of birth to the patient profile to continue.';

export const EMERGENCY_CONTACT_MOBILE_REQUIRED =
  'A phone number is required for each emergency contact';

export enum PatientGender {
  MALE = 'Male',
  FEMALE = 'Female',
  CIS_GENDER_FEMALE = 'Cis-gender Female',
  CIS_GENDER_MALE = 'Cis-gender Male',
  NON_BINARY = 'Non-binary',
  OTHER = 'Other',
  TRANSGENDER_FEMALE = 'Transgender female',
  TRANSGENDER_MALE = 'Transgender male',
  UNKNOWN = 'Unknown',
}

export enum TreatmentEpisodeStatus {
  IN_TREATMENT = 'In treatment',
  PAUSED = 'Paused',
  DISCHARGED = 'Discharged',
  NEVER_STARTED = 'Never started',
  UNKNOWN = 'Unknown',
  VOID = 'Void',
}
export enum LabOrderEligibleProviderType {
  Physician = 'Physician',
  NursePractitioner = 'Nurse Practitioner',
  Psychiatrist = 'Psychiatrist',
  PsychiatricNursePractitioner = 'Psychiatric Nurse Practitioner',
  PhysicianAssistant = 'Physician Assistant',
  RegisteredNurse = 'Registered Nurse',
}

export enum MedicationOrderEligibleProviderType {
  MedicalAssistant = 'Medical Assistant',
  NursePractitioner = 'Nurse Practitioner',
  Physician = 'Physician',
  PhysicianAssistant = 'Physician Assistant',
  PsychiatricNursePractitioner = 'Psychiatric Nurse Practitioner',
  Psychiatrist = 'Psychiatrist',
  RegisteredNurse = 'Registered Nurse',
}

export const disallowedUniversalStatusesForOrderingLab = [
  PatientUniversalStatusCode.CLOSED,
  PatientUniversalStatusCode.DISCHARGED,
  PatientUniversalStatusCode.PAUSED,
  PatientUniversalStatusCode.NEVER_STARTED,
  PatientUniversalStatusCode.UNKNOWN,
];

export const disallowedProviderTypesForOrderingLab = [
  'Admissions Operations',
  'Admissions Specialist',
  'Admissions Supervisor',
  'Analyst',
  'Billing Specialist',
  'Care Coordinator',
  'Client Success',
  'Clinical Ops',
  'Clinical Supervisor',
  'Dietitian',
  'Family Experience Navigator',
  'Family Mentor',
  'Insurance Specialist',
  'Medical Assistant',
  'Patient Service Representative',
  'Peer Mentor',
  'Practice Coordinator',
  'Product Support',
  'Provider Partnerships',
  'Recovery Navigator',
  'Therapist',
  'Recovery Coach',
  'Caregiver Coach',
  'Care Navigator',
];

export const intakeOnlyMedicalProviderTypeNames = new Set(
  [
    CareTeamProviderType.NURSE_PRACTITIONER,
    CareTeamProviderType.PHYSICIAN,
    CareTeamProviderType.PHYSICIAN_ASSISTANT,
  ].map((t) => CareTeamProviderTypeNames[t]),
);
// TODO remove need for this constant
//  it's used twice and the layout would be simpler if the sidebar just contained both the header andcontent
export const PATIENT_MESSAGES_SIDEBAR_WIDTH = '350px';

export const PATIENT_MEDICATIONS_TAB_HEADING = 'Medications';
export const MEDICATIONS_UNAVAILABLE_MESSAGE =
  'Medications are currently unavailable';
export const SCRIPTSURE_PROVIDER_NOT_REGISTERED_MESSAGE =
  'Activate your Scriptsure account to begin prescribing medications';
export const SCRIPTSURE_PATIENT_NOT_FOUND_MESSAGE =
  "This patient doesn't have a profile for medication management yet. Please contact Equip Support for assistance.";
export const SCRIPTSURE_PRESCRIPTION_LOAD_ERROR_MESSAGE =
  "We were unable to load the patient's prescription information. Please refresh the page or try again later.";
